import { applyGhostStyle } from '../../plugin-sdk/ghost_preview.mjs';
import { normalizeSidebarRows } from '../../plugin-contracts/sidebar_list_contract.mjs';
import { uid } from '../utils/common_constants.js?v=20260706d';
import {
  appPerfEnd,
  appPerfStep,
  appPerfTimer
} from '../render/render_scheduler.js?v=20260706e';
import { S } from '../app/app_state.js?v=20260817c';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import {
  state,
  activeSystem,
  allSystems,
  getSystem,
  getSystemRegistryRevision
} from './system_registry_core.js?v=20260904f';
import { activateSystem } from './system_navigation.js?v=20260904f';
import { collectDrawingObjectSnapshot as collectCachedDrawingObjectSnapshot } from '../objects/drawing_object_snapshot_service.js?v=20260904f';
import { markProjectModelChanged } from '../project/project_revision_service.js';
import { hasPointerCommitClaim } from '../interaction/pointer_commit_ownership.mjs';
import {
  SYSTEM_EXPORT_LIMITS,
  detachedBoundaryValue,
  frozenBoundaryError,
  normalizeExcludedSystemIds,
  normalizeOwned3DPerformanceStats,
  normalizeOwnedProjectObjects,
  normalizeOwnedStepExportItems,
  normalizeOwnedWireframeExportItems
} from '../export/system_export_contract.mjs?v=20260904f';

const SYSTEM_PLAN_LAYER_SELECTOR = '.product-system-plan-layer[data-system-id]';

function reportInteractionHookFailure(systemId, hookName, error) {
  console.warn(`[system-boundary] Hook ${hookName} von ${systemId || '(unbekannt)'} ist fehlgeschlagen.`, error);
}

function safeSystemHook(system, hookName, invoke, fallback = null) {
  try {
    const result = invoke();
    if (isolateUnexpectedThenable(system, hookName, result)) return fallback;
    return result;
  } catch (error) {
    reportInteractionHookFailure(system?.id, hookName, error);
    return fallback;
  }
}

function safeBooleanSystemHook(system, hookName, invoke) {
  return safeSystemHook(system, hookName, invoke, null) === true;
}

function getSafeSystemHook(system, hookName) {
  const hook = safeSystemHook(system, `${hookName}:getter`, () => system?.[hookName], null);
  return typeof hook === 'function' ? hook : null;
}

function isolateUnexpectedThenable(system, hookName, value) {
  if (!value || (typeof value !== 'object' && typeof value !== 'function')) return false;
  let then;
  try {
    then = value.then;
  } catch (error) {
    reportInteractionHookFailure(system?.id, `${hookName}:thenable`, error);
    return true;
  }
  if (typeof then !== 'function') return false;
  try {
    Promise.resolve(value).then(
      () => undefined,
      error => reportInteractionHookFailure(system?.id, `${hookName}:thenable`, error)
    );
  } catch (error) {
    reportInteractionHookFailure(system?.id, `${hookName}:thenable`, error);
  }
  return true;
}

function dataOption(options, key, fallback = undefined) {
  if (!options || typeof options !== 'object' || Array.isArray(options)) return fallback;
  try {
    const descriptor = Object.getOwnPropertyDescriptor(options, key);
    return descriptor && Object.hasOwn(descriptor, 'value') ? descriptor.value : fallback;
  } catch {
    return fallback;
  }
}

function systemExportBoundaryOptions(options = {}, defaultTimeoutMs = SYSTEM_EXPORT_LIMITS.dxfReadinessTimeoutMs) {
  const projectSource = dataOption(options, 'project', S?.project);
  const detachedProject = detachedBoundaryValue(projectSource);
  if (detachedProject.issue) {
    console.warn('[system-export] Der Projektkontext konnte nicht sicher abgetrennt werden.', detachedProject.issue);
  }
  const excluded = normalizeExcludedSystemIds(dataOption(options, 'excludeSystemIds', []));
  const excludedIds = new Set(excluded.flatMap(value => [value, value.toLowerCase()]));
  const timeoutOption = dataOption(options, 'timeoutMs', defaultTimeoutMs);
  const requestedTimeout = typeof timeoutOption === 'number'
    ? timeoutOption
    : typeof timeoutOption === 'string' && /^\d+(?:\.\d+)?$/.test(timeoutOption.trim())
      ? Number(timeoutOption)
      : defaultTimeoutMs;
  const timeoutMs = Number.isFinite(requestedTimeout)
    ? Math.min(120_000, Math.max(1, Math.round(requestedTimeout)))
    : defaultTimeoutMs;
  return Object.freeze({
    project: detachedProject.value,
    excludedIds,
    timeoutMs
  });
}

function isSystemExcludedFromExport(system, excludedIds) {
  const identities = [system?.id, ...(Array.isArray(system?.aliases) ? system.aliases : [])]
    .map(value => String(value || '').trim())
    .filter(Boolean);
  return identities.some(value => excludedIds.has(value) || excludedIds.has(value.toLowerCase()));
}

function systemExportContext(system, boundaryOptions) {
  return Object.freeze({
    owner: system.id,
    ownerId: system.id,
    ownerSystemId: system.id,
    systemId: system.id,
    project: boundaryOptions.project
  });
}

function reportSystemExportIssues(system, hookName, issues = []) {
  if (!Array.isArray(issues) || issues.length === 0) return;
  console.warn(
    `[system-export] ${issues.length} ungueltige Ausgabe(n) aus ${hookName} von ${system.id} wurden verworfen.`,
    issues.slice(0, 3)
  );
}

function systemOwnerOptions(system) {
  return {
    ownerId: system.id,
    ownerAliases: Array.isArray(system.aliases) ? system.aliases : []
  };
}

function readinessTimeoutError(systemId, timeoutMs, kind = 'DXF') {
  const error = new Error(`${kind}-Bereitschaft von ${systemId} hat das Zeitlimit von ${timeoutMs} ms ueberschritten.`);
  error.code = `${kind}_READINESS_TIMEOUT`;
  return error;
}

async function invokeReadinessHook(system, hook, context, timeoutMs, kind = 'DXF') {
  let timer = null;
  const invocation = Promise.resolve().then(() => hook.call(system, context));
  const timeout = new Promise((_resolve, reject) => {
    timer = setTimeout(() => reject(readinessTimeoutError(system.id, timeoutMs, kind)), timeoutMs);
  });
  try {
    return await Promise.race([invocation, timeout]);
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

// Optional readiness result: at most a few short, plain warning texts.
function readinessWarnings(value) {
  const warnings = value && typeof value === 'object' && !Array.isArray(value) ? value.warnings : null;
  if (!Array.isArray(warnings)) return Object.freeze([]);
  return Object.freeze(warnings.slice(0, 10)
    .filter(text => typeof text === 'string' && text.trim())
    .map(text => text.trim().slice(0, 300)));
}

function isPlainInteractionRecord(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  try {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

function ownedHit(system, hit) {
  if (!isPlainInteractionRecord(hit)) return null;
  const clone = cloneBoundaryData(hit);
  return clone ? Object.freeze({ ...clone, owner: system.id, systemId: system.id }) : null;
}

function systemSelectionFields(system, options = {}) {
  return [
    ...(system?.multiSelectionFields || []),
    ...(options.includePassthrough === true ? (system?.boxSelectionPassthroughFields || []) : [])
  ];
}

function cloneBoundaryData(value, depth = 0) {
  if (value == null || ['string', 'number', 'boolean'].includes(typeof value)) return value;
  if (depth > 12) return null;
  if (Array.isArray(value)) {
    return Object.freeze(value.map(entry => cloneBoundaryData(entry, depth + 1)));
  }
  if (!isPlainInteractionRecord(value)) return null;
  const clone = {};
  for (const [key, entry] of Object.entries(value)) clone[key] = cloneBoundaryData(entry, depth + 1);
  return Object.freeze(clone);
}

function ownedSelectionView(system, selection = {}, options = {}) {
  const source = isPlainInteractionRecord(selection) ? selection : {};
  const view = {};
  for (const field of systemSelectionFields(system, options)) {
    view[field] = Array.isArray(source[field]) ? cloneBoundaryData(source[field]) : Object.freeze([]);
  }
  return Object.freeze(view);
}

function systemHasSelection(system, selection = {}, options = {}) {
  return systemSelectionFields(system, options)
    .some(field => Array.isArray(selection?.[field]) && selection[field].length > 0);
}

function foreignSelectionCount(system, selection = {}) {
  const ownFields = new Set(systemSelectionFields(system, { includePassthrough: true }));
  const knownFields = new Set([
    'obstacleIds',
    ...allSystems().flatMap(entry => systemSelectionFields(entry, { includePassthrough: true }))
  ]);
  let count = 0;
  for (const field of knownFields) {
    if (ownFields.has(field) || !Array.isArray(selection?.[field])) continue;
    count += selection[field].length;
  }
  return count;
}

function ownedSelectionResult(system, result, options = {}) {
  if (!isPlainInteractionRecord(result)) return null;
  const normalized = {};
  for (const field of systemSelectionFields(system, options)) {
    try {
      if (!Array.isArray(result[field])) continue;
      normalized[field] = result[field].map(entry => cloneBoundaryData(entry));
    } catch (error) {
      reportInteractionHookFailure(system.id, `selection-result:${field}`, error);
    }
  }
  return Object.keys(normalized).length ? normalized : null;
}

function ownedEntityView(system, value) {
  if (!isPlainInteractionRecord(value)) return null;
  try {
    const declaredOwners = [value.systemId, value.systemKey, value.systemType, value.owner]
      .map(entry => String(entry || '').trim())
      .filter(Boolean);
    if (declaredOwners.length && declaredOwners.some(owner => owner !== system.id)) return null;
    const clone = cloneBoundaryData(value);
    if (!clone) return null;
    return Object.freeze({
      ...clone,
      owner: system.id,
      systemId: system.id,
      systemKey: system.id,
      systemType: system.id
    });
  } catch (_error) {
    return null;
  }
}

function ownerScopedContext(system, context = {}, options = {}) {
  const scoped = { ...context };
  const sourceSelection = context.selection || context.multiSelection || null;
  if (context.selection) scoped.selection = ownedSelectionView(system, context.selection, options);
  if (context.multiSelection) scoped.multiSelection = ownedSelectionView(system, context.multiSelection, options);
  if (context.targetSelection) scoped.targetSelection = ownedSelectionView(system, context.targetSelection, options);
  scoped.foreignSelectionCount = foreignSelectionCount(system, sourceSelection || {});
  scoped.hasForeignSelection = scoped.foreignSelectionCount > 0;
  scoped.systemId = system.id;
  scoped.system = system;
  return scoped;
}

function bindActiveDragOwner(system) {
  let drag;
  try {
    drag = S.drag;
    if (!drag || typeof drag !== 'object' || drag.active !== true) return false;
    drag.systemId = system.id;
    drag.owner = system.id;
    if (drag.systemId === system.id && drag.owner === system.id) return true;
  } catch (error) {
    reportInteractionHookFailure(system?.id, 'bindActiveDragOwner', error);
  }

  // Ein nicht beschreibbarer oder fehlerhafter Drag darf nicht mit einem
  // fremden Owner weiterleben. Der sichere Fallback beendet nur diesen Drag;
  // alle anderen Interaktionsdaten bleiben ausserhalb dieses Objekts erhalten.
  try {
    S.drag = { active: false, type: null, systemId: null, owner: null };
  } catch (error) {
    reportInteractionHookFailure(system?.id, 'cancelUnownedDrag', error);
  }
  return false;
}

function createOwnedMutableSnapshot(system, snapshot, count) {
  const owned = { ...snapshot, count };
  Object.defineProperty(owned, 'systemId', {
    configurable: false,
    enumerable: true,
    value: system.id,
    writable: false
  });
  return owned;
}

function ownerScopedClipboardContext(system, context = {}) {
  const scoped = ownerScopedContext(system, context);
  for (const key of ['items', 'payload', 'preview', 'delta', 'target', 'world', 'rawWorld']) {
    if (!(key in context)) continue;
    scoped[key] = cloneBoundaryData(context[key]);
  }
  const kind = String(context.kind || '').trim();
  if (kind && Array.isArray(context[kind])) scoped[kind] = cloneBoundaryData(context[kind]);
  return scoped;
}

function outerSystemPlanLayer(target) {
  let layer = target?.closest?.(SYSTEM_PLAN_LAYER_SELECTOR) || null;
  if (!layer) return null;
  for (let ancestor = layer.parentElement; ancestor; ancestor = ancestor.parentElement) {
    if (ancestor.matches?.(SYSTEM_PLAN_LAYER_SELECTOR)) layer = ancestor;
  }
  return layer;
}

/**
 * Beschreibt hit Test2 D im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} systemId - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} clientX - Eingabewert für hit Test2 D.
 * @param {*} clientY - Eingabewert für hit Test2 D.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function hitTest2D(systemId, clientX, clientY) {
  const system = getSystem(systemId);
  if (!system?.hitTest2D) return null;
  const profile = appPerfTimer(`SystemRegistry.hitTest2D:${systemId}`);
  try {
    const hit = safeSystemHook(system, 'hitTest2D', () => system.hitTest2D(clientX, clientY), null);
    return safeSystemHook(system, 'hitTest2D:result', () => ownedHit(system, hit), null);
  } finally {
    appPerfEnd(profile, 16);
  }
}

/**
 * Verarbeitet handle Click im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function handleClick(event, context = {}) {
  const system = context.systemId
    ? getSystem(context.systemId)
    : context.hit?.systemId
      ? getSystem(context.hit.systemId)
      : activeSystem();
  if (typeof system?.handleClick !== 'function') return false;
  const handled = safeBooleanSystemHook(system, 'handleClick', () => system.handleClick(ownerScopedContext(system, {
    ...context,
    event,
    hit: context.hit ? ownedHit(system, context.hit) : null
  })));
  if (handled && system.id !== state.activeSystemId && system.meta?.globalSystem !== true) {
    activateSystem(system.id);
  }
  return handled;
}

/**
 * Verarbeitet handle Double Click im Bereich Kernanwendung.
 *
 * Zweck:
 * - routet einen neutralen Doppelklick samt Hit-Kontext an das betroffene System
 * - ueberlaesst Auswahlbereich und Auswahlmutation vollstaendig dem System
 * - kennt dadurch weder produktbezogene IDs noch Gruppen-/Geometrieregeln
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function handleDoubleClick(event, context = {}) {
  const system = context.systemId
    ? getSystem(context.systemId)
    : context.hit?.systemId
      ? getSystem(context.hit.systemId)
      : activeSystem();
  if (typeof system?.handleDoubleClick !== 'function') return false;
  return safeBooleanSystemHook(system, 'handleDoubleClick', () => system.handleDoubleClick(ownerScopedContext(system, {
    ...context,
    event,
    hit: context.hit ? ownedHit(system, context.hit) : null
  })));
}

/**
 * Verarbeitet handle Pointer Event im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} kind - Eingabewert für handle Pointer Event.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function handlePointerEvent(kind, event, context = {}) {
  const dragOwnerId = kind !== 'down' && S.drag?.active
    ? String(S.drag?.systemId || S.drag?.owner || '').trim()
    : '';
  const system = dragOwnerId
    ? getSystem(dragOwnerId)
    : context.systemId
      ? getSystem(context.systemId)
      : activeSystem();
  if (!system) return false;
  const handler = kind === 'down'
    ? system.handlePointerDown
    : kind === 'move'
      ? system.handlePointerMove
      : kind === 'up'
        ? system.handlePointerUp
        : null;
  if (typeof handler !== 'function') return false;
  const hadActiveDrag = S.drag?.active === true;
  const commitDrag = S.drag;
  const commitProject = S.project;
  const commitSheetId = S.drawingSheets?.activeSheetId;
  const commitView = S.project?.currentView;
  const handled = safeBooleanSystemHook(system, `handlePointer${kind[0].toUpperCase()}${kind.slice(1)}`, () => handler(ownerScopedContext(system, {
    ...context,
    event,
    hit: context.hit ? ownedHit(system, context.hit) : null
  })));
  // Auch ein Hook, der nach dem Anlegen seines Drags `false` liefert oder
  // wirft, darf keinen fremden/ownerlosen Drag im globalen Zustand lassen.
  bindActiveDragOwner(system);
  // Pointer-Moves sind nur eine fluessige Vorschau. Abgeleitete Snap-,
  // Mess-, Link- und Drawing-Object-Indizes werden erst beim Commit erneuert;
  // andernfalls invalidiert jedes einzelne Maus-Pixel den gesamten Projektcache.
  if (handled && hadActiveDrag && kind === 'up'
    && !hasPointerCommitClaim(commitDrag, system.id, commitProject, commitSheetId)) {
    markProjectModelChanged(`${system.id}:pointer-${kind}`);
    // Einige Hooks rendern synchron vor ihrer Rueckkehr. Erst danach sind
    // auch abschliessende Snap-/Merge-Mutationen vollstaendig; ein gemeinsam
    // geplanter Refresh muss deshalb die neue Revision sehen.
    if (S.project === commitProject
      && S.drawingSheets?.activeSheetId === commitSheetId
      && commitView === 'plan'
      && S.project?.currentView === commitView) {
      AppRuntime.requestAppRefresh(false, `${system.id}:pointer-up-commit`);
    }
  }
  return handled;
}

/**
 * Verarbeitet handle Pointer Down im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function handlePointerDown(event, context = {}) {
  return handlePointerEvent('down', event, context);
}

/**
 * Verarbeitet handle Pointer Move im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function handlePointerMove(event, context = {}) {
  return handlePointerEvent('move', event, context);
}

/**
 * Verarbeitet handle Pointer Up im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function handlePointerUp(event, context = {}) {
  return handlePointerEvent('up', event, context);
}

/**
 * Beschreibt hit Test Order im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function hitTestOrder() {
  const activeId = state.activeSystemId;
  const ids = allSystems().map(system => system.id);
  return [
    activeId,
    ...ids.filter(id => id !== activeId)
  ].filter((id, index, list) => id && list.indexOf(id) === index);
}

export function preferredHitSystemFromTarget(target) {
  if (!target?.closest) return null;
  const hostLayer = outerSystemPlanLayer(target);
  if (hostLayer) {
    const hostOwner = String(hostLayer.getAttribute?.('data-system-id') || '').trim();
    return getSystem(hostOwner)?.id || null;
  }
  const active = activeSystem();
  if (typeof active?.ownsDomTarget === 'function'
    && safeBooleanSystemHook(active, 'ownsDomTarget', () => active.ownsDomTarget(target))) return active.id;
  return null;
}

export function hitTestPlan(clientX, clientY, context = {}) {
  const preferredSystemId = context.preferredSystemId
    || context.systemId
    || preferredHitSystemFromTarget(context.target);
  const order = hitTestOrder();
  const orderedSystemIds = preferredSystemId
    ? [preferredSystemId]
    : order;
  const profile = appPerfTimer('SystemRegistry.hitTestPlan');
  for (const systemId of orderedSystemIds) {
    const hit = hitTest2D(systemId, clientX, clientY);
    appPerfStep(profile, systemId || 'system');
    if (hit) {
      appPerfEnd(profile, 16);
      return hit;
    }
  }
  appPerfEnd(profile, 16);
  return null;
}

function systemsForRouting(context = {}) {
  const preferredId = context.systemId || preferredHitSystemFromTarget(context.target);
  const activeId = state.activeSystemId;
  const order = preferredId ? [preferredId] : [activeId].filter(Boolean);
  return order.map(id => getSystem(id)).filter(Boolean);
}

export function handleClickBeforeHitTest(event, context = {}) {
  for (const system of systemsForRouting({ ...context, target: context.target || event?.target })) {
    if (typeof system.wantsClickBeforeHitTest !== 'function') continue;
    const hookContext = ownerScopedContext(system, { ...context, event });
    if (!safeBooleanSystemHook(system, 'wantsClickBeforeHitTest', () => system.wantsClickBeforeHitTest(hookContext))) continue;
    if (typeof system.handleClick !== 'function') return false;
    if (safeBooleanSystemHook(system, 'handleClick', () => system.handleClick({ ...hookContext, hit: null }))) return true;
  }
  return false;
}

export function shouldSuppressPointerDownTarget(event, context = {}) {
  for (const system of systemsForRouting({ ...context, target: context.target || event?.target })) {
    if (typeof system.shouldSuppressPointerDownTarget !== 'function') continue;
    if (safeBooleanSystemHook(system, 'shouldSuppressPointerDownTarget', () => system.shouldSuppressPointerDownTarget(
      ownerScopedContext(system, { ...context, event })
    ))) return true;
  }
  return false;
}

export function handlePointerDownBeforeSelection(event, context = {}) {
  for (const system of systemsForRouting({ ...context, target: context.target || event?.target })) {
    if (typeof system.wantsPointerDownBeforeSelection !== 'function') continue;
    const hookContext = ownerScopedContext(system, { ...context, event });
    const wantsPointerDown = safeBooleanSystemHook(system, 'wantsPointerDownBeforeSelection', () => (
      system.wantsPointerDownBeforeSelection(hookContext)
    ));
    bindActiveDragOwner(system);
    if (!wantsPointerDown) continue;
    if (typeof system.handlePointerDown !== 'function') return false;
    const handled = safeBooleanSystemHook(system, 'handlePointerDown', () => (
      system.handlePointerDown(hookContext)
    ));
    bindActiveDragOwner(system);
    return handled;
  }
  return false;
}

export function cancelContextMenu(event, context = {}) {
  for (const system of systemsForRouting({ ...context, target: context.target || event?.target })) {
    if (typeof system.wantsContextMenuCancel !== 'function') continue;
    if (!safeBooleanSystemHook(system, 'wantsContextMenuCancel', () => system.wantsContextMenuCancel(
      ownerScopedContext(system, { ...context, event })
    ))) continue;
    return cancelInteraction(system.id, { event, ...context }) === true;
  }
  return false;
}

export function hitBelongsToSelection(hit, selection, context = {}) {
  const system = hit?.systemId ? getSystem(hit.systemId) : null;
  if (typeof system?.hitBelongsToSelection !== 'function') return false;
  return safeBooleanSystemHook(system, 'hitBelongsToSelection', () => system.hitBelongsToSelection(ownerScopedContext(system, {
    ...context,
    hit: ownedHit(system, hit),
    selection
  })));
}

export function wantsPointerMoveFrame(context = {}) {
  const activeId = String(context.systemId || S.drag?.systemId || S.drag?.owner || state.activeSystemId || '').trim();
  const system = getSystem(activeId);
  if (typeof system?.wantsPointerMoveFrame !== 'function') return false;
  return safeBooleanSystemHook(system, 'wantsPointerMoveFrame', () => system.wantsPointerMoveFrame(ownerScopedContext(system, {
    ...context,
    activeSystemId: state.activeSystemId
  })));
}

// Systeme koennen einen Drag-Typ bewusst ueber ein schlichtes Mouseup hinaus
// aktiv halten. Der globale Handler fragt dafuer nur den aktuellen Owner,
// statt fachliche Drag-Namen im Core zu kennen.
export function dragTypeSurvivesPointerUp(dragType) {
  if (!dragType) return false;
  const ownerId = String(S.drag?.systemId || S.drag?.owner || state.activeSystemId || '').trim();
  const system = getSystem(ownerId);
  if (typeof system?.dragTypeSurvivesPointerUp !== 'function') return false;
  return safeBooleanSystemHook(system, 'dragTypeSurvivesPointerUp', () => (
    system.dragTypeSurvivesPointerUp(dragType)
  ));
}

export function clearPointerPreview(context = {}) {
  let cleared = false;
  for (const system of allSystems()) {
    if (typeof system.clearPointerPreview !== 'function') continue;
    if (safeBooleanSystemHook(system, 'clearPointerPreview', () => system.clearPointerPreview(
      ownerScopedContext(system, context)
    ))) cleared = true;
  }
  return cleared;
}

/**
 * Rendert draw Interaction Overlays im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
export function drawInteractionOverlays() {
  for (const system of allSystems()) {
    if (system.drawInteractionOverlay) {
      safeSystemHook(system, 'drawInteractionOverlay', () => system.drawInteractionOverlay(
        ownerScopedContext(system)
      ));
    }
  }
}

/**
 * Erzeugt create Bom Rows im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Artikel-, Katalog- und Stücklistenaufbereitung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * - Verdichtet technische Artikelinformationen für Anzeige, Export oder Stückliste.
 * @param {*} groupIds - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function createBomRows(groupIds) {
  const system = activeSystem();
  const createRows = system?.getBomItems || system?.createBomRows;
  if (typeof createRows !== 'function') return [];
  try {
    return ownedBomRows(system, createRows(groupIds));
  } catch (error) {
    reportBomHookFailure(system.id, 'createBomRows', error);
    return [];
  }
}

function isPlainBomRecord(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  try {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

function reportBomHookFailure(systemId, hook, error) {
  console.warn(`[bom] System-Hook ${hook} ist fehlgeschlagen`, systemId, error);
}

function ownedBomRows(system, rows) {
  if (!Array.isArray(rows)) return [];
  const ownedRows = [];
  for (const row of rows) {
    if (!isPlainBomRecord(row)) continue;
    try {
      ownedRows.push({
        ...row,
        sourceSystemId: system.id
      });
    } catch (error) {
      reportBomHookFailure(system.id, 'createBomRows:row', error);
    }
  }
  return ownedRows;
}

function ownedBomScopeId(systemId, value) {
  const owner = String(systemId || '').trim();
  const localId = String(value || '').trim();
  if (!owner || !localId) return '';
  return localId.startsWith(`${owner}:`) ? localId : `${owner}:${localId}`;
}

/**
 * Sammelt collect Bom Rows im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Artikel-, Katalog- und Stücklistenaufbereitung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * - Verdichtet technische Artikelinformationen für Anzeige, Export oder Stückliste.
 * @param {*} scopeBySystem - Eingabewert für collect Bom Rows.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function collectBomRows(scopeBySystem = {}) {
  const collectedRows = [];
  for (const system of allSystems()) {
    const createRows = system.getBomItems || system.createBomRows;
    if (typeof createRows !== 'function') continue;
    try {
      const scopeIds = Array.isArray(scopeBySystem?.[system.id]) ? scopeBySystem[system.id] : [];
      if (!scopeIds.length) continue;
      collectedRows.push(...ownedBomRows(system, createRows(scopeIds)));
    } catch (error) {
      reportBomHookFailure(system.id, 'createBomRows', error);
    }
  }
  return collectedRows;
}

export function getBomSortRank(row = {}, context = {}) {
  const fallbackRank = Number.isFinite(Number(context.fallbackRank)) ? Number(context.fallbackRank) : 1000;
  let sourceSystemId;
  try {
    sourceSystemId = String(row?.sourceSystemId || '').trim();
  } catch {
    return fallbackRank;
  }
  const system = sourceSystemId ? getSystem(sourceSystemId) : null;
  if (typeof system?.getBomSortRank !== 'function') return fallbackRank;

  try {
    const ownedRow = Object.freeze({ ...row, sourceSystemId: system.id });
    const rank = system.getBomSortRank({ ...context, system, row: ownedRow });
    if (rank === null || rank === undefined || rank === false || rank === '') return fallbackRank;
    if (Number.isFinite(Number(rank))) return Number(rank);
  } catch (error) {
    reportBomHookFailure(system.id, 'getBomSortRank', error);
  }

  return fallbackRank;
}

export function getCanvasCommandStatus(context = {}) {
  let requestedSystemId;
  try {
    requestedSystemId = String(context?.systemId || '').trim();
  } catch (error) {
    reportInteractionHookFailure(null, 'getCanvasCommandStatus:context', error);
    return '';
  }
  const system = requestedSystemId ? getSystem(requestedSystemId) : activeSystem();
  if (!system) return '';
  const hook = safeSystemHook(
    system,
    'getCanvasCommandStatus:getter',
    () => system.getCanvasCommandStatus,
    null
  );
  if (typeof hook !== 'function') return '';
  const hookContext = safeSystemHook(system, 'getCanvasCommandStatus:context', () => {
    const source = context && (typeof context === 'object' || typeof context === 'function') ? context : {};
    const scoped = ownerScopedContext(system, {
      ...source,
      state,
      project: S.project,
      ui: S.ui,
      selection: S.selection
    });
    return Object.freeze({
      ...scoped,
      owner: system.id,
      systemId: system.id,
      system
    });
  }, null);
  if (!hookContext) return '';
  const status = safeSystemHook(
    system,
    'getCanvasCommandStatus',
    () => hook.call(system, hookContext),
    null
  );
  if (isolateUnexpectedThenable(system, 'getCanvasCommandStatus', status)) return '';
  return typeof status === 'string' ? status.trim() : '';
}

export function syncActiveToolFromLegacy(context = {}) {
  const activeSystemId = context.activeSystemId || state.activeSystemId || null;
  const orderedSystems = [
    activeSystemId ? getSystem(activeSystemId) : null,
    ...allSystems().filter(system => system.id !== activeSystemId)
  ].filter(Boolean);

  for (const system of orderedSystems) {
    if (typeof system?.syncActiveToolFromLegacy !== 'function') continue;
    const descriptor = safeSystemHook(system, 'syncActiveToolFromLegacy', () => system.syncActiveToolFromLegacy({
      ...context,
      activeSystemId,
      state,
      project: S.project,
      ui: S.ui,
      selection: ownedSelectionView(system, S.selection),
      systemId: system.id,
      system
    }), null);
    if (!isPlainInteractionRecord(descriptor) || !descriptor.tool) continue;
    return Object.freeze({
      owner: system.id,
      tool: String(descriptor.tool),
      meta: isPlainInteractionRecord(descriptor.meta) ? cloneBoundaryData(descriptor.meta) : Object.freeze({})
    });
  }
  return null;
}

/**
 * Sammelt collect Bom Scopes im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Artikel-, Katalog- und Stücklistenaufbereitung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * - Verdichtet technische Artikelinformationen für Anzeige, Export oder Stückliste.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function collectBomScopes() {
  const collectedScopes = [];
  for (const system of allSystems()) {
    if (typeof system.getBomScopes !== 'function') continue;
    try {
      const scopes = system.getBomScopes(S?.project);
      if (!Array.isArray(scopes)) continue;
      for (const scope of scopes) {
        if (!isPlainBomRecord(scope)) continue;
        try {
          const id = ownedBomScopeId(
            system.id,
            scope.id || scope.sourceId || scope.name || uid('bom_scope')
          );
          if (!id) continue;
          collectedScopes.push({
            ...scope,
            id,
            systemId: system.id
          });
        } catch (error) {
          reportBomHookFailure(system.id, 'getBomScopes:scope', error);
        }
      }
    } catch (error) {
      reportBomHookFailure(system.id, 'getBomScopes', error);
    }
  }
  return collectedScopes;
}

/**
 * Sammelt collect Project Objects im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function collectProjectObjects(options = {}) {
  const boundaryOptions = systemExportBoundaryOptions(options);
  const collected = [];
  for (const system of allSystems()) {
    if (isSystemExcludedFromExport(system, boundaryOptions.excludedIds)) continue;
    const hook = getSafeSystemHook(system, 'getProjectObjects');
    if (!hook) continue;
    const objects = safeSystemHook(
      system,
      'getProjectObjects',
      () => hook.call(system, systemExportContext(system, boundaryOptions)),
      null
    );
    if (objects === null || objects === undefined) continue;
    const normalized = normalizeOwnedProjectObjects(objects, systemOwnerOptions(system));
    reportSystemExportIssues(system, 'getProjectObjects', normalized.issues);
    collected.push(...normalized.entries);
  }
  return Object.freeze(collected);
}

// Projektweiter Wireframe-/STEP-Export (view3d_controller.js
// exportProjectWireframe()/exportProjectStep()): jedes System, das echte
// wireframe_3d.dxf/STEP-Quelldateien fuer seine platzierten Objekte hat,
// kann sich hier ueber getWireframeExportItems()/getStepExportItems()
// (gleiches Muster wie getProjectObjects()) anmelden, statt dass der
// Exporter einzelne Produktsysteme kennen muss.
export function collectWireframeExportItems(options = {}) {
  const boundaryOptions = systemExportBoundaryOptions(options);
  const collected = [];
  for (const system of allSystems()) {
    if (isSystemExcludedFromExport(system, boundaryOptions.excludedIds)) continue;
    const hook = getSafeSystemHook(system, 'getWireframeExportItems');
    if (!hook) continue;
    const items = safeSystemHook(
      system,
      'getWireframeExportItems',
      () => hook.call(system, systemExportContext(system, boundaryOptions)),
      null
    );
    if (items === null || items === undefined) continue;
    const normalized = normalizeOwnedWireframeExportItems(items, systemOwnerOptions(system));
    reportSystemExportIssues(system, 'getWireframeExportItems', normalized.issues);
    collected.push(...normalized.entries);
  }
  return Object.freeze(collected);
}

export function collectStepExportItems(options = {}) {
  const boundaryOptions = systemExportBoundaryOptions(options);
  const collected = [];
  for (const system of allSystems()) {
    if (isSystemExcludedFromExport(system, boundaryOptions.excludedIds)) continue;
    const hook = getSafeSystemHook(system, 'getStepExportItems');
    if (!hook) continue;
    const items = safeSystemHook(
      system,
      'getStepExportItems',
      () => hook.call(system, systemExportContext(system, boundaryOptions)),
      null
    );
    if (items === null || items === undefined) continue;
    const normalized = normalizeOwnedStepExportItems(items, systemOwnerOptions(system));
    reportSystemExportIssues(system, 'getStepExportItems', normalized.issues);
    collected.push(...normalized.entries);
  }
  return Object.freeze(collected);
}

// Muss vor dem projektweiten DXF-Export (dxf_export.js buildProjectDxf())
// abgewartet werden: ein System, dessen getProjectObjects()' dxfShapes von
// einem lazy nachgeladenen Artefakt abhaengen (z.B. dxf_entities.json,
// asynchron nachgeladen), meldet sich hier ueber ensureDxfShapesReady() an
// -- ohne das faellt ein einmaliger Download-Klick beim ERSTEN Aufruf auf
// die grobe Bounding-Box zurueck, nur weil der Fetch noch nicht fertig war.
export async function ensureAllDxfShapesReady(options = {}) {
  const boundaryOptions = systemExportBoundaryOptions(options);
  const participants = allSystems().flatMap(system => {
    if (isSystemExcludedFromExport(system, boundaryOptions.excludedIds)) return [];
    const hook = getSafeSystemHook(system, 'ensureDxfShapesReady');
    return hook ? [{ system, hook }] : [];
  });
  const statuses = await Promise.all(participants.map(async ({ system, hook }) => {
    try {
      await invokeReadinessHook(
        system,
        hook,
        systemExportContext(system, boundaryOptions),
        boundaryOptions.timeoutMs
      );
      return Object.freeze({
        owner: system.id,
        systemId: system.id,
        status: 'ready'
      });
    } catch (error) {
      reportInteractionHookFailure(system.id, 'ensureDxfShapesReady', error);
      return Object.freeze({
        owner: system.id,
        systemId: system.id,
        status: 'failed',
        error: frozenBoundaryError(error)
      });
    }
  }));
  const readySystemIds = Object.freeze(statuses
    .filter(status => status.status === 'ready')
    .map(status => status.systemId));
  const failedSystemIds = Object.freeze(statuses
    .filter(status => status.status === 'failed')
    .map(status => status.systemId));
  return Object.freeze({
    ok: failedSystemIds.length === 0,
    statuses: Object.freeze(statuses),
    readySystemIds,
    failedSystemIds
  });
}

// Muss vor dem projektweiten STEP-Export (view3d_controller.js
// exportProjectStep()) abgewartet werden: ein System, dessen STEP-Quellen erst
// bei Bedarf erzeugt werden (z. B. Laengenvarianten), meldet sich ueber
// ensureStepExportReady() an. Anders als bei DXF bleiben die STEP-Beitraege
// eines nicht bereiten Systems im Export: sie sind eigenstaendige Platzierungen,
// die Bereitschaft verbessert nur ihre Quellen. Ein System kann optional
// { warnings: [...] } liefern; Fehler und Zeitueberschreitungen werden gemeldet.
export async function ensureAllStepExportReady(options = {}) {
  const boundaryOptions = systemExportBoundaryOptions(options, SYSTEM_EXPORT_LIMITS.stepReadinessTimeoutMs);
  const participants = allSystems().flatMap(system => {
    if (isSystemExcludedFromExport(system, boundaryOptions.excludedIds)) return [];
    const hook = getSafeSystemHook(system, 'ensureStepExportReady');
    return hook ? [{ system, hook }] : [];
  });
  const statuses = await Promise.all(participants.map(async ({ system, hook }) => {
    try {
      const value = await invokeReadinessHook(system, hook, systemExportContext(system, boundaryOptions),
        boundaryOptions.timeoutMs, 'STEP');
      return Object.freeze({ owner: system.id, systemId: system.id, status: 'ready', warnings: readinessWarnings(value) });
    } catch (error) {
      reportInteractionHookFailure(system.id, 'ensureStepExportReady', error);
      return Object.freeze({ owner: system.id, systemId: system.id, status: 'failed', warnings: Object.freeze([]),
        error: frozenBoundaryError(error) });
    }
  }));
  const failedSystemIds = Object.freeze(statuses.filter(status => status.status === 'failed').map(status => status.systemId));
  return Object.freeze({
    ok: failedSystemIds.length === 0 && statuses.every(status => status.warnings.length === 0),
    statuses: Object.freeze(statuses),
    failedSystemIds,
    warnings: Object.freeze(statuses.flatMap(status => status.warnings))
  });
}

function collectDrawingObjectsFromSystems() {
  const collected = [];
  const provenanceByObject = new WeakMap();
  let cacheable = true;
  for (const system of allSystems()) {
    if (typeof system.getDrawingObjects !== 'function') continue;
    const objects = safeSystemHook(
      system,
      'getDrawingObjects',
      () => system.getDrawingObjects(),
      null
    );
    if (!Array.isArray(objects)) {
      cacheable = false;
      continue;
    }
    let length;
    try {
      length = objects.length;
    } catch (error) {
      cacheable = false;
      console.warn(`[drawing-objects] DrawingObject-Liste von System ${system.id} konnte nicht gelesen werden.`, error);
      continue;
    }
    for (let index = 0; index < length; index += 1) {
      try {
        const object = objects[index];
        if (!object || typeof object !== 'object') continue;
        const declaredOwners = [object.systemType, object.systemId, object.owner]
          .map(value => String(value || '').trim())
          .filter(Boolean);
        if (!declaredOwners.length || declaredOwners.some(ownerId => ownerId !== system.id)) {
          console.warn(`[drawing-objects] DrawingObject mit ungueltigem Owner von System ${system.id} verworfen.`);
          continue;
        }
        provenanceByObject.set(object, Object.freeze({
          systemId: system.id,
          objectId: String(object.id || '').trim()
        }));
        collected.push(object);
      } catch (error) {
        cacheable = false;
        console.warn(`[drawing-objects] DrawingObject von System ${system.id} konnte nicht gelesen werden.`, error);
      }
    }
  }
  return { objects: collected, provenanceByObject, cacheable };
}

export function collectDrawingObjectSnapshot(options = {}) {
  return collectCachedDrawingObjectSnapshot({
    force: options.force === true,
    projectIdentity: S.project,
    systemRevision: getSystemRegistryRevision(),
    collector: collectDrawingObjectsFromSystems
  });
}

export function collectDrawingObjects(options = {}) {
  return collectDrawingObjectSnapshot(options).objects;
}

export function collectCoreObjects() {
  return allSystems().flatMap(system => {
    if (typeof system.getCoreObjects !== 'function') return [];
    const objects = safeSystemHook(system, 'getCoreObjects', () => system.getCoreObjects(S?.project), []);
    if (!Array.isArray(objects)) return [];
    return objects.flatMap(object => {
      const owned = ownedEntityView(system, object);
      return owned ? [Object.freeze({
        ...owned,
        productLabel: owned.productLabel || system.label || system.name || system.id
      })] : [];
    });
  });
}

export function collectSidebarRows() {
  return allSystems().flatMap(system => {
    if (typeof system.getSidebarRows !== 'function') return [];
    return safeSystemHook(system, 'getSidebarRows', () => normalizeSidebarRows(system.id,
      system.getSidebarRows(Object.freeze({ systemId: system.id, owner: system.id }))), []);
  });
}

function sidebarRowAction(systemId, rowId, hook, visible) {
  const system = getSystem(systemId);
  if (typeof system?.[hook] !== 'function' || typeof system.getSidebarRows !== 'function') return false;
  return safeBooleanSystemHook(system, hook, () => {
    const row = normalizeSidebarRows(system.id, system.getSidebarRows(Object.freeze({ systemId, owner: systemId })))
      .find(entry => entry.id === rowId);
    if (!row) return false;
    return system[hook](Object.freeze({ systemId, owner: systemId, row,
      ...(hook === 'setSidebarRowVisibility' ? { visible: visible !== false } : {}) }));
  });
}
export function selectSidebarRow(systemId, rowId) {
  return sidebarRowAction(systemId, rowId, 'selectSidebarRow');
}
export function setSidebarRowVisibility(systemId, rowId, visible) {
  return sidebarRowAction(systemId, rowId, 'setSidebarRowVisibility', visible);
}

export function collectObjectLibraryItems(options = {}) {
  const sourceSystems = options.activeSystemOnly === true
    ? [activeSystem()].filter(Boolean)
    : allSystems();
  const collected = [];
  for (const system of sourceSystems) {
    if (typeof system.getObjectLibraryItems !== 'function') continue;
    const items = safeSystemHook(system, 'getObjectLibraryItems', () => system.getObjectLibraryItems(ownerScopedContext(system, {
      system,
      project: S.project,
      ui: S.ui,
      selection: S.selection
    })), null);
    if (isolateUnexpectedThenable(system, 'getObjectLibraryItems', items)) continue;
    const length = safeSystemHook(system, 'getObjectLibraryItems:list', () => (
      Array.isArray(items) ? items.length : 0
    ), 0);
    for (let index = 0; index < length; index += 1) {
      const owned = safeSystemHook(system, `getObjectLibraryItems:item:${index}`, () => {
        const item = cloneBoundaryData(items[index]);
        if (!isPlainInteractionRecord(item)) return null;
        const rawId = item.id || item.action;
        const id = ['string', 'number'].includes(typeof rawId) && String(rawId).trim()
          ? String(rawId).trim()
          : uid('object_library_item');
        const rawProductLabel = item.productLabel || system.label || system.name || system.id;
        const productLabel = ['string', 'number'].includes(typeof rawProductLabel)
          ? String(rawProductLabel).trim()
          : String(system.id);
        return Object.freeze({
          ...item,
          id,
          productLabel: productLabel || system.id,
          owner: system.id,
          systemId: system.id,
          systemKey: system.id,
          systemType: system.id
        });
      }, null);
      if (owned) collected.push(owned);
    }
  }
  return collected;
}

export function collectProjectBounds() {
  const collected = [];
  for (const system of allSystems()) {
    if (!system.getProjectBounds) continue;
    const rawBounds = safeSystemHook(
      system,
      'getProjectBounds',
      () => system.getProjectBounds(S?.project),
      null
    );
    if (isolateUnexpectedThenable(system, 'getProjectBounds', rawBounds)) continue;
    const bounds = safeSystemHook(
      system,
      'getProjectBounds:result',
      () => cloneBoundaryData(rawBounds),
      null
    );
    const entries = Array.isArray(bounds) ? bounds : bounds ? [bounds] : [];
    for (let index = 0; index < entries.length; index += 1) {
      const owned = safeSystemHook(
        system,
        `getProjectBounds:entry:${index}`,
        () => ownedEntityView(system, entries[index]),
        null
      );
      if (owned) collected.push(owned);
    }
  }
  return collected;
}

export function collect3DSceneSignatures(options = {}) {
  const boundaryOptions = systemExportBoundaryOptions(options);
  const collected = [];
  for (const system of allSystems()) {
    if (isSystemExcludedFromExport(system, boundaryOptions.excludedIds)) continue;
    const hook = getSafeSystemHook(system, 'get3DSceneSignature');
    if (!hook) continue;
    const signature = safeSystemHook(
      system,
      'get3DSceneSignature',
      () => hook.call(system, boundaryOptions.project),
      null
    );
    if (signature === null || signature === undefined) continue;
    const detached = detachedBoundaryValue(signature);
    if (detached.issue) {
      reportSystemExportIssues(system, 'get3DSceneSignature', [detached.issue]);
      continue;
    }
    collected.push(Object.freeze({
      signature: detached.value,
      owner: system.id,
      ownerId: system.id,
      ownerSystemId: system.id,
      systemId: system.id,
      systemKey: system.id,
      systemType: system.id,
      sourceSystemId: system.id,
      systemLabel: system.label || system.name || system.id
    }));
  }
  return Object.freeze(collected);
}

export function collect3DPerformanceStats(options = {}) {
  const boundaryOptions = systemExportBoundaryOptions(options);
  const collected = [];
  for (const system of allSystems()) {
    if (isSystemExcludedFromExport(system, boundaryOptions.excludedIds)) continue;
    const hook = getSafeSystemHook(system, 'get3DPerformanceStats');
    if (!hook) continue;
    const stats = safeSystemHook(
      system,
      'get3DPerformanceStats',
      () => hook.call(system, systemExportContext(system, boundaryOptions)),
      null
    );
    if (stats === null || stats === undefined) continue;
    const normalized = normalizeOwned3DPerformanceStats(stats, systemOwnerOptions(system));
    reportSystemExportIssues(system, 'get3DPerformanceStats', normalized.issues);
    for (const entry of normalized.entries) {
      collected.push(Object.freeze({
        ...entry,
        owner: system.id,
        ownerId: system.id,
        ownerSystemId: system.id,
        systemId: system.id,
        systemKey: system.id,
        systemType: system.id,
        sourceSystemId: system.id,
        systemLabel: system.label || system.name || system.id
      }));
    }
  }
  return Object.freeze(collected);
}

/**
 * Sammelt collect Snap Points im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Snap-Beziehungen, Fangpunkte und Persistenz zwischen Elementen, damit Aufrufer nicht die Moduldetails kennen müssen.
 * - Bewahrt die fachliche Beziehung zwischen Kontaktpunkten, Wandachsen und gespeicherten Snap-Daten.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function collectSnapPoints(context = {}) {
  const profile = appPerfTimer('SystemRegistry.collectSnapPoints');
  const points = [];
  try {
    for (const system of allSystems()) {
      if (!system.getSnapPoints) continue;
      const systemProfile = appPerfTimer(`SystemRegistry.getSnapPoints:${system.id || 'unknown'}`);
      try {
        const rawSystemPoints = safeSystemHook(system, 'getSnapPoints', () => system.getSnapPoints({
          ...context,
          project: S.project,
          systemId: system.id,
          system
        }), []);
        const systemPoints = safeSystemHook(
          system,
          'getSnapPoints:result',
          () => cloneBoundaryData(rawSystemPoints),
          []
        );
        if (!Array.isArray(systemPoints)) continue;
        for (let index = 0; index < systemPoints.length; index += 1) {
          const owned = safeSystemHook(system, `getSnapPoints:point:${index}`, () => {
            const point = systemPoints[index];
            if (!isPlainInteractionRecord(point)) return null;
            return Object.freeze({
              ...point,
              owner: system.id,
              systemId: system.id,
              systemKey: system.id,
              systemType: system.id
            });
          }, null);
          if (owned) points.push(owned);
        }
      } finally {
        appPerfEnd(systemProfile, 16);
        appPerfStep(profile, system.id || 'system');
      }
    }
    return points;
  } finally {
    appPerfEnd(profile);
  }
}

/**
 * Sammelt collect Box Selections im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} rect - Eingabewert für collect Box Selections.
 * @param {*} mode - Eingabewert für collect Box Selections.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function collectBoxSelections(rect, mode = 'window', context = {}) {
  const result = {};
  for (const system of allSystems()) {
    if (typeof system.collectBoxSelection !== 'function') continue;
    const selection = safeSystemHook(system, 'collectBoxSelection', () => system.collectBoxSelection(rect, mode, {
      ...context,
      project: S.project,
      systemId: system.id,
      system
    }), null);
    const owned = ownedSelectionResult(system, selection, { includePassthrough: true });
    if (!owned) continue;
    for (const [field, entries] of Object.entries(owned)) {
      if (!Array.isArray(result[field])) result[field] = [];
      result[field].push(...entries);
    }
  }
  return result;
}

export function selectBoxSelection(selection, context = {}) {
  const orderedSystems = allSystems()
    .filter(system => typeof system.selectBoxSelection === 'function')
    .sort((a, b) => a.selectionPriority - b.selectionPriority);
  for (const system of orderedSystems) {
    if (!systemHasSelection(system, selection, { includePassthrough: true })) continue;
    if (safeBooleanSystemHook(system, 'selectBoxSelection', () => system.selectBoxSelection({
      ...context,
      selection: ownedSelectionView(system, selection, { includePassthrough: true }),
      hasForeignSelection: foreignSelectionCount(system, selection) > 0,
      systemId: system.id,
      system
    }))) return true;
  }
  return false;
}

export function mergeLegacyMultiSelection(targetSelection = {}, legacySelection = {}) {
  const legacySnapshot = cloneBoundaryData(isPlainInteractionRecord(legacySelection) ? legacySelection : {});
  for (const system of allSystems()) {
    if (typeof system.mergeLegacySelection !== 'function') continue;
    const owned = {};
    for (const field of systemSelectionFields(system)) {
      owned[field] = Array.isArray(targetSelection[field]) ? [...targetSelection[field]] : [];
    }
    safeSystemHook(system, 'mergeLegacySelection', () => system.mergeLegacySelection(owned, legacySnapshot));
    for (const field of systemSelectionFields(system)) {
      if (Array.isArray(owned[field])) targetSelection[field] = [...owned[field]];
    }
  }
  return targetSelection;
}

export function syncLegacyMultiSelection(selection = {}, legacySelection = {}) {
  for (const system of allSystems()) {
    if (typeof system.syncLegacySelectionFields !== 'function') continue;
    safeSystemHook(system, 'syncLegacySelectionFields', () => system.syncLegacySelectionFields(
      ownedSelectionView(system, selection),
      legacySelection
    ));
  }
  return legacySelection;
}

export function syncLegacyMultiSelectionForOwner(systemId, selection = {}, legacySelection = {}) {
  const system = getSystem(systemId || '');
  if (typeof system?.syncLegacySelectionFields !== 'function') return false;
  safeSystemHook(system, 'syncLegacySelectionFields', () => system.syncLegacySelectionFields(
    ownedSelectionView(system, selection),
    legacySelection
  ));
  return true;
}

export function resolveItemToMultiSelection(item) {
  const system = getSystem(String(item?.owner || '').trim());
  if (typeof system?.resolveItemToMultiSelection !== 'function') return null;
  const result = safeSystemHook(system, 'resolveItemToMultiSelection', () => (
    system.resolveItemToMultiSelection(cloneBoundaryData({ ...item, owner: system.id }))
  ), null);
  if (!isPlainInteractionRecord(result) || !systemSelectionFields(system).includes(result.field)) return null;
  const ref = result.ref !== undefined ? cloneBoundaryData(result.ref) : undefined;
  const id = result.id !== undefined ? cloneBoundaryData(result.id) : undefined;
  if (ref === undefined && !id) return null;
  return Object.freeze({ field: result.field, ...(ref !== undefined ? { ref } : { id }) });
}

export function resolveHitToMultiSelection(hit) {
  const system = getSystem(String(hit?.systemId || '').trim());
  if (typeof system?.resolveHitToMultiSelection !== 'function') return null;
  const result = safeSystemHook(system, 'resolveHitToMultiSelection', () => (
    system.resolveHitToMultiSelection(ownedHit(system, hit))
  ), null);
  if (!isPlainInteractionRecord(result) || !systemSelectionFields(system).includes(result.field)) return null;
  const id = cloneBoundaryData(result.id);
  const ref = result.ref !== undefined ? cloneBoundaryData(result.ref) : undefined;
  if (!id && ref === undefined) return null;
  return Object.freeze({ field: result.field, ...(ref !== undefined ? { ref } : { id }) });
}

export function resolveHitToSelectionItem(hit) {
  const system = getSystem(String(hit?.systemId || '').trim());
  if (typeof system?.resolveHitToItems !== 'function') return null;
  const result = safeSystemHook(system, 'resolveHitToItems', () => (
    system.resolveHitToItems(ownedHit(system, hit))
  ), null);
  if (!isPlainInteractionRecord(result)) return null;
  const id = String(result.id || '').trim();
  const kind = String(result.kind || '').trim();
  if (!id || !kind) return null;
  return Object.freeze({ ...cloneBoundaryData(result), owner: system.id, kind, id });
}

export function deduplicateMultiSelectionField(field, values = []) {
  const system = allSystems().find(entry => systemSelectionFields(entry).includes(field));
  const fallback = [...new Set(Array.isArray(values) ? values : [])].filter(Boolean);
  if (typeof system?.deduplicateMultiSelectionField !== 'function') return fallback;
  const result = safeSystemHook(system, 'deduplicateMultiSelectionField', () => (
    system.deduplicateMultiSelectionField(field, cloneBoundaryData(fallback))
  ), fallback);
  return Array.isArray(result) ? [...new Set(result)].filter(Boolean) : fallback;
}

/**
 * Entfernt delete Selection im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} systemId - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function deleteSelection(systemId, context = {}) {
  const system = systemId
    ? getSystem(systemId)
    : activeSystem();
  if (typeof system?.deleteSelection !== 'function') return false;
  return safeBooleanSystemHook(system, 'deleteSelection', () => (
    system.deleteSelection(ownerScopedContext(system, context))
  ));
}

/**
 * Beschreibt nudge Selection im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} systemId - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function nudgeSelection(systemId, context = {}) {
  const system = systemId
    ? getSystem(systemId)
    : activeSystem();
  if (typeof system?.nudgeSelection !== 'function') return false;
  return safeBooleanSystemHook(system, 'nudgeSelection', () => (
    system.nudgeSelection(ownerScopedContext(system, context))
  ));
}

function systemsForSelectionDispatch(preferredSystemId = null, selection = {}) {
  const activeId = preferredSystemId || state.activeSystemId;
  const itemOwners = Array.isArray(S.selection?.items)
    ? S.selection.items.map(item => String(item?.owner || '').trim()).filter(owner => owner && owner !== 'core')
    : [];
  const selectedOwners = allSystems()
    .filter(system => systemHasSelection(system, selection, { includePassthrough: true }))
    .map(system => system.id);
  return [
    activeId,
    ...itemOwners,
    ...selectedOwners
  ]
    .filter((id, index, list) => id && list.indexOf(id) === index)
    .map(id => getSystem(id))
    .filter(Boolean);
}

export function deleteFirstSelection(context = {}) {
  const selection = context.selection || context.multiSelection || {};
  // Preserve the current owner intent even when its runtime is unavailable.
  // Each hook receives only its detached owner items through ownerScopedContext.
  const hookContext = { ...context, items: context.items || S.selection?.items || [] };
  for (const system of systemsForSelectionDispatch(context.systemId || null, selection)) {
    if (typeof system.deleteSelection !== 'function') continue;
    if (safeBooleanSystemHook(system, 'deleteSelection', () => (
      system.deleteSelection(ownerScopedContext(system, { ...hookContext,
        items: Object.freeze((Array.isArray(hookContext.items) ? hookContext.items : [])
          .filter(item => item?.owner === system.id)
          .map(item => ownedEntityView(system, item)).filter(Boolean))
      }))
    ))) return true;
  }
  return false;
}

export function nudgeFirstSelection(context = {}) {
  const selection = context.selection || context.multiSelection || {};
  for (const system of systemsForSelectionDispatch(context.systemId || null, selection)) {
    if (typeof system.nudgeSelection !== 'function') continue;
    if (safeBooleanSystemHook(system, 'nudgeSelection', () => (
      system.nudgeSelection(ownerScopedContext(system, context))
    ))) return true;
  }
  return false;
}

export function deleteObjects(systemId, context = {}) {
  const system = getSystem(systemId);
  if (typeof system?.deleteObjects !== 'function') return 0;
  const deleted = safeSystemHook(system, 'deleteObjects', () => (
    system.deleteObjects(ownerScopedContext(system, context))
  ), 0);
  return Number.isFinite(Number(deleted)) ? Math.max(0, Number(deleted)) : deleted === true ? 1 : 0;
}

export function deleteObjectsForSelection(context = {}, options = {}) {
  const selection = context.selection || context.multiSelection || {};
  if (options.requireAll && (context.items || []).some(item => item?.owner !== 'core' && !getSystem(item?.owner))) return false;
  let count = 0;
  const owners = [...new Set((context.items || []).map(item => item?.owner).filter(owner => owner && owner !== 'core'))];
  const systems = allSystems();
  if (options.requireAll) systems.sort((a, b) => {
    const rank = system => owners.includes(system.id) ? owners.indexOf(system.id) : owners.length;
    return rank(a) - rank(b);
  });
  for (const system of systems) {
    const items = Object.freeze((Array.isArray(context.items) ? context.items : [])
      .filter(item => item?.owner === system.id)
      .map(item => ownedEntityView(system, item)).filter(Boolean));
    if (!systemHasSelection(system, selection) && !items.length) continue;
    if (typeof system.deleteObjects !== 'function') {
      if (options.requireAll) return false;
      continue;
    }
    const project = S.project;
    const before = options.requireAll ? JSON.stringify(project) : null;
    const deleted = safeSystemHook(system, 'deleteObjects', () => (
      system.deleteObjects(ownerScopedContext(system, { ...context, items }))
    ), 0);
    const numeric = options.requireAll && typeof deleted !== 'number' && deleted !== true ? NaN : Number(deleted);
    const normalized = Number.isFinite(numeric) ? Math.max(0, numeric) : 0;
    // Strict dispatch is used only inside the host's detached transaction.
    // An unsupported/rejected/no-op owner must not delete the other owners.
    if (options.requireAll && (!normalized || S.project !== project || before === JSON.stringify(S.project))) return false;
    count += normalized;
  }
  return count;
}

export function createMoveSnapshots(context = {}) {
  const selection = context.selection || context.multiSelection || {};
  return allSystems()
    .map(system => {
      if (typeof system.createMoveSnapshot !== 'function' || !systemHasSelection(system, selection)) return null;
      const snapshot = safeSystemHook(
        system,
        'createMoveSnapshot',
        () => system.createMoveSnapshot(ownerScopedContext(system, context)),
        null
      );
      return safeSystemHook(system, 'createMoveSnapshot:result', () => {
        if (!isPlainInteractionRecord(snapshot)) return null;
        const count = Number.isFinite(Number(snapshot.count)) ? Math.max(0, Number(snapshot.count)) : 0;
        return count ? createOwnedMutableSnapshot(system, snapshot, count) : null;
      }, null);
    })
    .filter(Boolean);
}

export function applyMoveSnapshot(snapshot, context = {}) {
  const system = getSystem(snapshot?.systemId || '');
  if (typeof system?.applyMoveSnapshot !== 'function') return 0;
  const moved = safeSystemHook(system, 'applyMoveSnapshot', () => system.applyMoveSnapshot({
    ...ownerScopedContext(system, context),
    snapshot
  }), 0);
  const count = Number.isFinite(Number(moved)) ? Math.max(0, Number(moved)) : moved === true ? (snapshot.count || 1) : 0;
  if (count > 0) markProjectModelChanged(`${system.id}:move-snapshot`);
  return count;
}

export function syncMoveSelection(snapshot, context = {}) {
  const system = getSystem(snapshot?.systemId || '');
  if (typeof system?.syncMoveSelection !== 'function') return null;
  const selection = safeSystemHook(system, 'syncMoveSelection', () => system.syncMoveSelection({
    ...ownerScopedContext(system, context),
    snapshot
  }), null);
  return ownedSelectionResult(system, selection);
}

export function applyMovePreview(snapshot, context = {}) {
  const system = getSystem(snapshot?.systemId || '');
  if (typeof system?.applyMovePreview !== 'function') return false;
  return safeBooleanSystemHook(system, 'applyMovePreview', () => system.applyMovePreview({
    ...ownerScopedContext(system, context),
    snapshot
  }));
}

export function clearMovePreview(snapshot, context = {}) {
  const system = getSystem(snapshot?.systemId || '');
  if (typeof system?.clearMovePreview !== 'function') return false;
  return safeBooleanSystemHook(system, 'clearMovePreview', () => system.clearMovePreview({
    ...ownerScopedContext(system, context),
    snapshot
  }));
}

export function createClipboardPayload(systemId, context = {}) {
  const system = getSystem(systemId || '');
  if (typeof system?.createClipboardPayload !== 'function') return null;
  const payload = safeSystemHook(system, 'createClipboardPayload', () => (
    system.createClipboardPayload(ownerScopedClipboardContext(system, context))
  ), null);
  return isPlainInteractionRecord(payload) ? cloneBoundaryData(payload) : null;
}

export function pasteClipboardPayload(systemId, context = {}) {
  const system = getSystem(systemId || '');
  if (typeof system?.pasteClipboardPayload !== 'function') return null;
  const result = safeSystemHook(system, 'pasteClipboardPayload', () => (
    system.pasteClipboardPayload(ownerScopedClipboardContext(system, context))
  ), null);
  return ownedSelectionResult(system, result);
}

export function createClipboardPreview(systemId, context = {}) {
  const system = getSystem(systemId || '');
  if (typeof system?.createClipboardPreview !== 'function') return null;
  const preview = safeSystemHook(system, 'createClipboardPreview', () => (
    system.createClipboardPreview(ownerScopedClipboardContext(system, context))
  ), null);
  if (!isPlainInteractionRecord(preview)) return null;
  const clone = safeSystemHook(system, 'createClipboardPreview:result', () => ({
    ...cloneBoundaryData(preview),
    owner: system.id,
    systemId: system.id
  }), null);
  return clone ? Object.freeze(clone) : null;
}

export function renderClipboardPreview(systemId, context = {}) {
  const system = getSystem(systemId || '');
  if (typeof system?.renderClipboardPreview !== 'function') return false;
  const target = context.parentLayer;
  const documentRef = target?.ownerDocument || globalThis.document;
  if (!target?.appendChild || typeof documentRef?.createElementNS !== 'function') return false;
  const ownerLayer = documentRef.createElementNS('http://www.w3.org/2000/svg', 'g');
  ownerLayer.setAttribute('class', 'system-clipboard-preview-layer');
  ownerLayer.setAttribute('data-system-id', system.id);
  const rendered = safeBooleanSystemHook(system, 'renderClipboardPreview', () => system.renderClipboardPreview({
    ...ownerScopedClipboardContext(system, context),
    parentLayer: ownerLayer,
    systemId: system.id,
    system
  }));
  if (!rendered) {
    ownerLayer.remove?.();
    return false;
  }
  ownerLayer.setAttribute('class', 'system-clipboard-preview-layer');
  ownerLayer.setAttribute('data-system-id', system.id);
  applyGhostStyle(ownerLayer);
  target.appendChild(ownerLayer);
  return true;
}

export function selectClipboardObjects(systemId, context = {}) {
  const system = getSystem(systemId || '');
  if (typeof system?.selectClipboardObjects !== 'function') return false;
  const selection = ownedSelectionView(system, context.selection || context);
  return safeBooleanSystemHook(system, 'selectClipboardObjects', () => system.selectClipboardObjects({
    ...selection,
    selection,
    systemId: system.id,
    system
  }));
}

export function clipboardPasteEntriesLegacy(payload = {}) {
  if (!payload || typeof payload !== 'object') return [];
  const readonlyPayload = cloneBoundaryData(payload);
  return allSystems().flatMap(system => {
    if (system.legacyClipboardKind !== payload.kind || typeof system.getLegacyClipboardEntry !== 'function') return [];
    const entry = safeSystemHook(system, 'getLegacyClipboardEntry', () => system.getLegacyClipboardEntry(readonlyPayload), null);
    if (!isPlainInteractionRecord(entry) || !entry.key || !Array.isArray(entry.items) || !entry.items.length) return [];
    return [{
      systemId: system.id,
      key: String(entry.key),
      items: cloneBoundaryData(entry.items)
    }];
  });
}

export function pasteLegacyClipboardPayload(payload, offset) {
  const system = allSystems().find(s => s.legacyClipboardKind === payload?.kind);
  if (!system?.pasteLegacyPayload) return false;
  const result = safeSystemHook(system, 'pasteLegacyPayload', () => (
    system.pasteLegacyPayload(cloneBoundaryData(payload), offset)
  ), null);
  return result?.handled === true;
}

export function systemsByClipboardActivationPriority() {
  return allSystems()
    .filter(s => s.clipboardActivationPriority != null)
    .sort((a, b) => a.clipboardActivationPriority - b.clipboardActivationPriority);
}

export function buildClipboardEntries(context = {}) {
  return allSystems().flatMap(system => {
    if (typeof system.buildClipboardEntry !== 'function') return [];
    const selection = context.multiSelection || context.selection || {};
    if (!systemHasSelection(system, selection)) return [];
    const entry = safeSystemHook(system, 'buildClipboardEntry', () => (
      system.buildClipboardEntry(ownerScopedContext(system, context))
    ), null);
    if (!isPlainInteractionRecord(entry) || !entry.key || !Array.isArray(entry.items) || !entry.items.length) return [];
    return [{
      systemId: system.id,
      key: String(entry.key),
      items: cloneBoundaryData(entry.items)
    }];
  });
}

export function clipboardPasteEntries(payload = {}) {
  if (!payload || typeof payload !== 'object' || payload.kind !== 'mixedObjects') return [];
  if (Array.isArray(payload.systemEntries)) {
    return payload.systemEntries.flatMap(entry => {
      if (!isPlainInteractionRecord(entry)) return [];
      const system = getSystem(String(entry.systemId || '').trim());
      const key = String(entry.key || '').trim();
      if (!system || !key || !Array.isArray(entry.items) || !entry.items.length) return [];
      return [{ systemId: system.id, key, items: cloneBoundaryData(entry.items) }];
    });
  }
  const readonlyPayload = cloneBoundaryData(payload);
  return allSystems().flatMap(system => {
    if (typeof system.getClipboardEntry !== 'function') return [];
    const entry = safeSystemHook(system, 'getClipboardEntry', () => system.getClipboardEntry(readonlyPayload), null);
    if (!isPlainInteractionRecord(entry) || !entry.key || !Array.isArray(entry.items) || !entry.items.length) return [];
    return [{ systemId: system.id, key: String(entry.key), items: cloneBoundaryData(entry.items) }];
  });
}

export function buildActiveSelectionClipboardPayload() {
  for (const system of allSystems()) {
    if (typeof system.buildActiveSelectionClipboardPayload !== 'function') continue;
    const result = safeSystemHook(system, 'buildActiveSelectionClipboardPayload', () => (
      system.buildActiveSelectionClipboardPayload({ systemId: system.id, system })
    ), null);
    if (isPlainInteractionRecord(result) && isPlainInteractionRecord(result.payload)) {
      return Object.freeze({ ...result, payload: cloneBoundaryData(result.payload), systemId: system.id });
    }
  }
  return null;
}

export function selectCoreGroup(systemId, group, context = {}) {
  const system = getSystem(systemId || group?.systemId || group?.systemKey || '');
  if (typeof system?.selectCoreGroup !== 'function') return false;
  const ownedGroup = ownedEntityView(system, group);
  if (!ownedGroup) return false;
  return safeBooleanSystemHook(system, 'selectCoreGroup', () => system.selectCoreGroup(
    ownerScopedContext(system, { ...context, group: ownedGroup })
  ));
}

function normalizeCoreGroupSidebarConfirmation(confirm = null) {
  if (!confirm || typeof confirm !== 'object' || Array.isArray(confirm)) return null;
  const message = String(confirm.message || '').trim();
  if (!message) return null;
  return {
    title: String(confirm.title || 'Aktion bestätigen').trim() || 'Aktion bestätigen',
    message,
    confirmLabel: String(confirm.confirmLabel || 'Bestätigen').trim() || 'Bestätigen',
    cancelLabel: String(confirm.cancelLabel || 'Abbrechen').trim() || 'Abbrechen',
    danger: confirm.danger === true
  };
}

function normalizeCoreGroupSidebarAction(action = null) {
  if (!action || typeof action !== 'object' || Array.isArray(action)) return null;
  const id = String(action.id || '').trim();
  const label = String(action.label || '').trim();
  if (!id || !label) return null;
  const variant = ['default', 'warning', 'danger'].includes(String(action.variant || '').trim())
    ? String(action.variant).trim()
    : 'default';
  return {
    id,
    label,
    title: String(action.title || label).trim() || label,
    pendingLabel: String(action.pendingLabel || '').trim(),
    successMessage: String(action.successMessage || '').trim(),
    unavailableMessage: String(action.unavailableMessage || '').trim(),
    disabled: action.disabled === true,
    variant,
    confirm: normalizeCoreGroupSidebarConfirmation(action.confirm)
  };
}

/**
 * Liest optionale, systemspezifische Aktionen fuer die rechte Gruppenkarte.
 * Der Core normalisiert die sichtbaren Metadaten, damit Produktmodule weder
 * HTML noch Eventhandler in die generische Sidebar einschleusen muessen.
 */
export function getCoreGroupSidebarActions(systemId, group, context = {}) {
  const system = getSystem(systemId || group?.systemId || group?.systemKey || '');
  if (typeof system?.getCoreGroupSidebarActions !== 'function') return [];
  const ownedGroup = ownedEntityView(system, group);
  if (!ownedGroup) return [];
  let rawActions;
  try {
    rawActions = system.getCoreGroupSidebarActions(
      ownerScopedContext(system, { ...context, group: ownedGroup })
    );
  } catch (error) {
    console.warn('[groups/sidebar] Gruppenaktionen konnten nicht gelesen werden', system.id, error);
    return [];
  }
  if (!Array.isArray(rawActions)) return [];
  const actionIds = new Set();
  return rawActions
    .map(normalizeCoreGroupSidebarAction)
    .filter(action => {
      if (!action || actionIds.has(action.id)) return false;
      actionIds.add(action.id);
      return true;
    });
}

/**
 * Fuehrt ausschliesslich eine unmittelbar zuvor noch angebotene Gruppenaktion
 * aus. Die erneute Abfrage ist bewusst Teil des Wrappers: Auch nach einem
 * bestaetigenden Dialog kann sich Auswahl oder Produktzustand geaendert haben.
 */
export async function executeCoreGroupSidebarAction(systemId, group, actionId, context = {}) {
  const system = getSystem(systemId || group?.systemId || group?.systemKey || '');
  if (typeof system?.executeCoreGroupSidebarAction !== 'function') {
    return { handled: false, reason: 'unsupported' };
  }
  const normalizedActionId = String(actionId || '').trim();
  const action = getCoreGroupSidebarActions(system?.id, group, context)
    .find(candidate => candidate.id === normalizedActionId && candidate.disabled !== true);
  if (!action) return { handled: false, reason: 'unavailable' };
  const ownedGroup = ownedEntityView(system, group);
  if (!ownedGroup) return { handled: false, reason: 'owner-mismatch' };
  let result;
  try {
    result = await system.executeCoreGroupSidebarAction(ownerScopedContext(system, {
      ...context,
      group: ownedGroup,
      action: cloneBoundaryData(action),
      actionId: action.id
    }));
  } catch (error) {
    reportInteractionHookFailure(system.id, 'executeCoreGroupSidebarAction', error);
    return { handled: false, reason: 'hook-failed' };
  }
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    return { ...result, handled: result.handled !== false };
  }
  return { handled: result === true };
}

export function snapCoreGroupRotation(systemId, group, rotationDeg, context = {}) {
  const fallback = Number(rotationDeg);
  const system = getSystem(systemId || group?.systemId || group?.systemKey || '');
  if (typeof system?.snapCoreGroupRotation !== 'function') {
    return Number.isFinite(fallback) ? fallback : 0;
  }
  const ownedGroup = ownedEntityView(system, group);
  if (!ownedGroup) return Number.isFinite(fallback) ? fallback : 0;
  const resolved = Number(safeSystemHook(system, 'snapCoreGroupRotation', () => system.snapCoreGroupRotation(
    ownerScopedContext(system, { ...context, group: ownedGroup, rotationDeg: fallback })
  ), fallback));
  return Number.isFinite(resolved) ? resolved : (Number.isFinite(fallback) ? fallback : 0);
}

export function finalizeCoreGroupRotation(systemId, group, context = {}) {
  const system = getSystem(systemId || group?.systemId || group?.systemKey || '');
  if (typeof system?.finalizeCoreGroupRotation !== 'function') return false;
  const ownedGroup = ownedEntityView(system, group);
  if (!ownedGroup) return false;
  return safeBooleanSystemHook(system, 'finalizeCoreGroupRotation', () => system.finalizeCoreGroupRotation(
    ownerScopedContext(system, { ...context, group: ownedGroup })
  ));
}

export function selectCoreObject(systemId, object, context = {}) {
  const system = getSystem(systemId || object?.systemId || object?.systemKey || '');
  if (typeof system?.selectCoreObject !== 'function') return false;
  const ownedObject = ownedEntityView(system, object);
  if (!ownedObject) return false;
  return safeBooleanSystemHook(system, 'selectCoreObject', () => system.selectCoreObject(
    ownerScopedContext(system, { ...context, object: ownedObject })
  ));
}

export function setCoreObjectVisibility(systemId, object, visible, context = {}) {
  const system = getSystem(systemId || object?.systemId || object?.systemKey || '');
  if (typeof system?.setCoreObjectVisibility !== 'function') return false;
  const ownedObject = ownedEntityView(system, object);
  if (!ownedObject) return false;
  return safeBooleanSystemHook(system, 'setCoreObjectVisibility', () => system.setCoreObjectVisibility(
    ownerScopedContext(system, { ...context, object: ownedObject, visible: visible !== false })
  ));
}

/**
 * Prüft cancel Interaction im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} systemId - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} context - Laufzeitkontext mit View-, Pointer- oder Renderingdaten.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function cancelInteraction(systemId, context = {}) {
  const system = systemId
    ? getSystem(systemId)
    : activeSystem();
  if (typeof system?.cancelInteraction !== 'function') return false;
  return safeBooleanSystemHook(system, 'cancelInteraction', () => system.cancelInteraction({
    ...context,
    systemId: system.id,
    system
  }));
}

export function cancelInteractions(context = {}) {
  let handled = false;
  for (const system of allSystems()) {
    if (typeof system?.cancelInteraction !== 'function') continue;
    if (safeBooleanSystemHook(system, 'cancelInteraction', () => system.cancelInteraction({
      ...context,
      systemId: system.id,
      system
    }))) handled = true;
  }
  return handled;
}

export function clearTransientStates(context = {}) {
  let handled = false;
  for (const system of allSystems()) {
    if (typeof system?.clearTransientState !== 'function') continue;
    if (safeBooleanSystemHook(system, 'clearTransientState', () => system.clearTransientState({
      ...context,
      systemId: system.id,
      system
    }))) handled = true;
  }
  return handled;
}

function notifyProjectLevelsHook(hookName, context = {}) {
  let handled = false;
  for (const system of allSystems()) {
    if (typeof system?.[hookName] !== 'function') continue;
    if (safeBooleanSystemHook(system, hookName, () => system[hookName]({
      ...context,
      systemId: system.id,
      system
    }))) handled = true;
  }
  return handled;
}

export function beforeProjectLevelsChange(context = {}) {
  return notifyProjectLevelsHook('beforeProjectLevelsChange', context);
}

export function afterProjectLevelsChange(context = {}) {
  return notifyProjectLevelsHook('afterProjectLevelsChange', context);
}
